import type { ChatMessage, Preferences, Project, ShortlistedUnit } from './types';

const SESSION_KEY = 'naya_advisor_session_id';
const STATE_KEY = 'naya_advisor_state';

export interface PersistedAdvisorState {
  sessionId: string;
  messages: ChatMessage[];
  prefs: Preferences;
  shortlist: Project[];
  unitShortlist: ShortlistedUnit[];
  /** Project IDs the buyer explored via focus / questions (visit-day source of truth). */
  engagedProjectIds?: string[];
  bookedVisits?: import('./advisor-api').BookedVisit[];
  savedAt: number;
}

export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export function loadPersistedState(): PersistedAdvisorState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedAdvisorState;
    if (!parsed.sessionId || !Array.isArray(parsed.messages)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePersistedState(state: Omit<PersistedAdvisorState, 'savedAt'>): void {
  if (typeof window === 'undefined') return;
  const payload: PersistedAdvisorState = { ...state, savedAt: Date.now() };
  localStorage.setItem(STATE_KEY, JSON.stringify(payload));
  // Signed-in buyers sync this blob to their profile ("your board follows
  // you"). Event-based so the auth layer needs no hook into advisor state.
  window.dispatchEvent(new CustomEvent('naya:state-saved'));
}

/** Raw persisted blob + savedAt — for profile sync (push/pull verbatim). */
export function readRawPersistedState(): { raw: string; savedAt: number } | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STATE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PersistedAdvisorState;
    if (!parsed.sessionId) return null;
    return { raw, savedAt: parsed.savedAt ?? 0 };
  } catch {
    return null;
  }
}

/** Adopt a synced profile blob wholesale (cross-device restore). The caller
 *  reloads afterwards — hydration happens once at provider mount. */
export function writeRawPersistedState(raw: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const parsed = JSON.parse(raw) as PersistedAdvisorState;
    if (!parsed.sessionId || !Array.isArray(parsed.messages)) return false;
    localStorage.setItem(STATE_KEY, raw);
    localStorage.setItem(SESSION_KEY, parsed.sessionId);
    return true;
  } catch {
    return false;
  }
}

export function clearAdvisorSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(STATE_KEY);
}

/** New anonymous session → new buyer phone for NayaDesk / ConverseSpine. */
export function startNewAdvisorSession(): string {
  if (typeof window === 'undefined') return crypto.randomUUID();
  localStorage.removeItem(STATE_KEY);
  const id = crypto.randomUUID();
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

/** Map anonymous session → stable E.164-ish phone for Naya /chat idempotency. */
export function sessionToBuyerPhone(sessionId: string): string {
  const hex = sessionId.replace(/-/g, '').slice(0, 10);
  const suffix = hex.padEnd(10, '0').slice(0, 10);
  return `+9190${suffix}`;
}
